import { z } from 'zod';
import { LOCALES } from '../locales.js';
import { istDay, localeSchema } from './public.js';
/**
 * Analytics — phases.md Phase 16, PRD §25.
 *
 * Two contracts live here. The **ingestion** shape is what a reader's browser posts to
 * `POST /api/v1/public/events`; it is the only unauthenticated write in the whole API, so
 * every field is bounded and nothing in it is trusted. The **report** shape is what the
 * CMS asks of `/api/v1/admin/analytics/*`.
 *
 * What is deliberately *absent* from the ingestion shape is the point of the phase: there
 * is no user id, no cookie id, no client-generated visitor id and no place to put one. A
 * field that does not exist cannot be filled in later by a well-meaning patch.
 */
/**
 * The event vocabulary. A `varchar` in the database (widening a MySQL enum rewrites the
 * table), but a closed set here — an event type nobody rolls up is an event type nobody
 * reads, and silently accepting one would grow the raw table for nothing.
 */
export const ANALYTICS_EVENT_TYPES = [
    /** One per route change, including the first paint. Path only, never a query string. */
    'page_view',
    /** One per article per session, after the reader has actually stayed (see the client). */
    'article_view',
    /** `value` is the milestone reached: 25, 50, 75 or 100. */
    'scroll_depth',
    /** `value` is *active* seconds — a timer that pauses on blur, not wall clock. */
    'read_time',
    /** `meta.network` is the target: whatsapp, facebook, x, copy, native, print. */
    'share_click',
    /** Logged server-side by the search endpoint too; the client form carries no filters. */
    'search',
    'video_play',
    /** `value` is the percentage watched at the last checkpoint. */
    'video_progress',
    'video_complete',
    /** `value` is elapsed seconds; an upper bound on attention, see the client comment. */
    'video_watch',
    /** Phase 13's creatives report through the same pipe rather than their own endpoint. */
    'ad_impression',
    'ad_click',
];
/**
 * A site path with the query string and fragment already gone.
 *
 * Stripping happens in the schema rather than in the handler because a query string is
 * exactly where personal data arrives by accident — a search term, an email in a
 * newsletter link, a session token someone pasted. The one place that can guarantee it
 * never reaches the database is the parse step every event passes through.
 */
const pathSchema = z
    .string()
    .trim()
    .max(500)
    .transform((value) => value.split(/[?#]/)[0] ?? '/')
    .pipe(z.string().startsWith('/', 'Path must be site-relative').max(500));
/** Free-form per event type, but bounded: eight short keys, nothing nested, no essays. */
const metaSchema = z
    .record(z.union([z.string().max(200), z.number(), z.boolean()]))
    .refine((value) => Object.keys(value).length <= 8, { message: 'Too many metadata keys' });
export const analyticsEventSchema = z.object({
    type: z.enum(ANALYTICS_EVENT_TYPES),
    path: pathSchema,
    /** Absent on a page that is not a story; present makes the content reports possible. */
    articleId: z.number().int().positive().optional(),
    videoId: z.number().int().positive().optional(),
    /**
     * Bounded well below anything a real reader produces: a day of active reading is 86400
     * seconds, and the cap stops a broken timer from dominating an average.
     */
    value: z.number().int().min(0).max(86_400).optional(),
    meta: metaSchema.optional(),
});
/**
 * The batch envelope. `sendBeacon` gets one shot at delivery during page unload, so the
 * client queues events and flushes several at once; the endpoint is shaped for that
 * rather than for one request per event.
 *
 * `referrer` is the *document* referrer as the browser saw it, not the `Referer` header —
 * on a beacon the header is our own page, which would make every reader look like
 * internal traffic. The server keeps the host and discards the rest.
 */
export const analyticsBatchSchema = z.object({
    locale: localeSchema,
    referrer: z.string().trim().max(500).optional(),
    /**
     * Capped at 50: the client never queues more, and a body that claims to is either a
     * bug or someone testing how much they can insert with one request.
     */
    events: z.array(analyticsEventSchema).min(1).max(50),
});
// --- Reports -----------------------------------------------------------------
export const ANALYTICS_REPORTS = ['overview', 'content', 'video', 'advertising', 'search'];
/**
 * Every report takes the same range, so the date picker is written once and the
 * comparison is a property of the range rather than of each screen.
 *
 * `compare` asks for the immediately preceding window of the same length — the
 * comparison an editor means by "versus last week" without having to type two ranges.
 */
export const analyticsRangeQuerySchema = z
    .object({
    from: istDay('start'),
    to: istDay('end'),
    compare: z
        .union([z.literal('true'), z.literal('false'), z.boolean()])
        .transform((value) => value === true || value === 'true')
        .default(false),
    language: z.enum(LOCALES).optional(),
    /** `csv` streams the same figures as a download; every report supports it. */
    format: z.enum(['json', 'csv']).default('json'),
    limit: z.coerce.number().int().positive().max(100).default(20),
})
    .refine((range) => range.to.getTime() >= range.from.getTime(), {
    message: '`to` must not precede `from`',
    path: ['to'],
})
    .refine((range) => range.to.getTime() - range.from.getTime() <= 400 * 86_400_000, {
    message: 'Range must be 400 days or less',
    path: ['to'],
});
//# sourceMappingURL=analytics.js.map